import { queryOptions } from '@tanstack/react-query';
import { YVES_API_URL } from '@frodo/lib/services/http/shared/constants';
import ApiHttpClientService from '@frodo/lib/services/http/base/api-http-client.service';
import { MUTATION_KEYS, QUERY_KEYS } from '@frodo/lib/services/http/shared/queryKeys';
import type {
  ActionResponse,
  ProductInsuranceOffer,
} from '@frodo/models/types';
import { mutationOptions } from '@frodo/lib/utils/client';

const UNAVAILABLE_OFFER: ProductInsuranceOffer = {
  available: false,
  alreadyInCart: false,
  plan: null,
};

const isProductInsuranceOffer = (
  value: unknown,
): value is ProductInsuranceOffer => {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const offer = value as ProductInsuranceOffer;

  return typeof offer.available === 'boolean';
};

class ApiInsuranceClientService extends ApiHttpClientService {
  async fetchProductInsuranceOffer(
    sku: string,
  ): Promise<ProductInsuranceOffer> {
    try {
      const offer = await this.get<ProductInsuranceOffer>(
        YVES_API_URL.productInsurance(sku),
      );

      return isProductInsuranceOffer(offer) ? offer : UNAVAILABLE_OFFER;
    } catch {
      return UNAVAILABLE_OFFER;
    }
  }

  async addInsuranceToCart(sku: string): Promise<ActionResponse> {
    return this.post(YVES_API_URL.addInsuranceToCart, { sku });
  }

  async removeInsuranceFromCart(sku: string): Promise<ActionResponse> {
    return this.post(YVES_API_URL.removeInsuranceFromCart, { sku });
  }
}

export const apiInsuranceClientService = new ApiInsuranceClientService();

export const productInsuranceQuery = (sku: string) => {
  return queryOptions({
    queryKey: QUERY_KEYS.productInsurance(sku),
    queryFn: () => apiInsuranceClientService.fetchProductInsuranceOffer(sku),
    staleTime: 0,
    refetchOnMount: 'always',
    throwOnError: false,
    retry: false,
  });
};

const insuranceCartMutation = (
  mutationKey: readonly string[],
  mutationFn: (sku: string) => Promise<ActionResponse>,
) => {
  return mutationOptions<ActionResponse, unknown, { sku: string }>({
    mutationKey,
    mutationFn: ({ sku }: { sku: string }) => mutationFn(sku),
    scope: { id: 'cart' },
  });
};

export const addInsuranceToCartMutation = () =>
  insuranceCartMutation(MUTATION_KEYS.cartInsuranceAdd(), sku =>
    apiInsuranceClientService.addInsuranceToCart(sku),
  );

export const removeInsuranceFromCartMutation = () =>
  insuranceCartMutation(MUTATION_KEYS.cartInsuranceRemove(), sku =>
    apiInsuranceClientService.removeInsuranceFromCart(sku),
  );
